import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from "react-router-dom";
import {
  Nav,
  NavItem,
  NavList,
  NavExpandable,
  Page,
  PageSidebar,
  PageSidebarBody,
  BackToTop
} from '@patternfly/react-core';
import DashboardHeader from './DashboardHeader';
import { AuthContext } from "shared/context/AuthContext";
import SideBarMenuConfig from "./SidebarMenuConfig";

export const DashboardWrapperWithRouter = (props) => {
  const location = useLocation();
  return <DashboardWrapper {...props} location={location} />;
};

const DashboardWrapper = ({ children, state: appState, location, navigate }) => {
  const context = useContext(AuthContext);

  const [isSidebarOpen, setIsSidebarOpen] = useState(location.pathname !== "/");

  useEffect(() => {
    setIsSidebarOpen(location.pathname !== "/");
  }, [location.pathname]);

  useEffect(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);
  
  /* =============================
     HELPERS
  ============================== */
  const buildBasePath = () => {
    const organizationmenu_data = appState.organizationmenu_data;
    const OgrganizationMenu = organizationmenu_data?.organization_menu || [];
    const orgSymbol = OgrganizationMenu[0]?.organization_symbol?.toLowerCase() ||
      process.env.REACT_APP_ORGANIZATION_SYMBOL;
    const branch_symbol = appState.branch_symbol ||
      Object.keys(OgrganizationMenu[0]?.organization_branches || {})[0];
    return `/${orgSymbol}/${branch_symbol}`;
  };

  /* =============================
     RENDER MENU ITEMS RECURSIVE
  ============================== */
  const renderMenuItems = (
    items,
    organization_branch_qualifications,
    organization_branch_exercises_types
  ) => {
    const currentPath = location.pathname;
    const basePath = buildBasePath();
    const authUser = context.state.isAuthUser;

    return items.map(item => {
      if (item.authOnly && !authUser) return null;

      if (item.external && item.path) {
        return (
          <NavItem key={item.label}>
            <Link to={item.path} target="_blank" rel="noopener noreferrer">{item.label}</Link>
          </NavItem>
        );
      }

      if (item.path && !item.type) {
        const link = item.path.startsWith("http") ? item.path : `${basePath}${item.path}`;
        return (
          <NavItem key={item.label} itemId={link} isActive={currentPath === link}>
            <Link to={link}>{item.label}</Link>
          </NavItem>
        );
      }

      if (item.children) {
        const shouldExpand = item.isExpandable !== false;
        return (
          <NavExpandable key={item.label} title={item.label} isExpanded={shouldExpand}>
            {renderMenuItems(item.children, organization_branch_qualifications, organization_branch_exercises_types)}
          </NavExpandable>
        );
      }

      if (item.type === "exercises") {
        const shouldExpand = item.isExpandable !== false;
        return (
          <NavExpandable key={item.label} title={item.label} isExpanded={shouldExpand}>
            {Object.entries(organization_branch_exercises_types || {}).map(([category, exercises]) => (
              <NavExpandable key={category} title={category} isExpanded={shouldExpand}>
                {exercises.map(exercise => {
                  const link = `${basePath}/exercises/${exercise.exercise_type}`;
                  return (
                    <NavItem key={exercise.id} itemId={link} isActive={currentPath === link}>
                      <Link to={link}>{exercise.description}</Link>
                    </NavItem>
                  );
                })}
              </NavExpandable>
            ))}
          </NavExpandable>
        );
      }

      if (item.type === "qualifications") {
        return (
          <NavExpandable key={item.label} title={item.label} isExpanded>
            {organization_branch_qualifications?.map(q => {
              const link = `${basePath}/sheets/${q.qualification_symbol.toLowerCase()}/${q.qualification_id}`;
              return (
                <NavItem key={q.qualification_id} itemId={link} isActive={currentPath === link}>
                  <Link to={link}>{q.qualification_symbol}</Link>
                </NavItem>
              );
            })}
          </NavExpandable>
        );
      }

      return null;
    });
  };

  const OgrganizationMenu = appState.organizationmenu_data?.organization_menu || [];
  const branch_symbol = appState.branch_symbol || Object.keys(OgrganizationMenu[0]?.organization_branches || {})[0];
  const branchData = OgrganizationMenu[0]?.organization_branches?.[branch_symbol] || {};
  const { organization_branch_qualifications = [], organization_branch_exercises_types = {} } = branchData;

  const PageNav = (
    <Nav>
      <NavList>
        {renderMenuItems(Object.values(SideBarMenuConfig), organization_branch_qualifications, organization_branch_exercises_types)}
      </NavList>
    </Nav>
  );

  const sidebar = <PageSidebar isSidebarOpen={isSidebarOpen}><PageSidebarBody>{PageNav}</PageSidebarBody></PageSidebar>;

  return (
    <>
      <Page mainContainerId="scrollable-element"
        sidebar={sidebar}
        masthead={<DashboardHeader context={context} state={appState} location={location.pathname} navigate={navigate} />}
        isManagedSidebar  // <--- tutaj PF zarządza sidebar
      >
        {children}
        <BackToTop scrollableSelector="#scrollable-element" />
      </Page>
    </>
  );
};

export default DashboardWrapperWithRouter;